import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

/**
 * Background booking-status watcher.
 *
 * When a signed-in shopper has any bookings sitting at `pending_payment`
 * (common with UPI where the JS handler doesn't fire after the app roundtrip),
 * this watcher polls `/bookings/me` every 5 s and:
 *   - Fires a rich "◆ Order confirmed" toast the moment the webhook flips the
 *     status to `confirmed`.
 *   - If the browser supports it and the user has granted permission, also
 *     surfaces a native Notification so they see the update even if the tab
 *     is backgrounded (they typically switched away to the UPI app).
 *
 * Mount this once at the app root — it self-throttles and cleans up.
 */
export default function BookingConfirmedWatcher() {
  const { user } = useAuth();
  const knownRef = useRef(new Map()); // booking_id -> last-seen status
  const permAskedRef = useRef(false);

  useEffect(() => {
    if (!user) { knownRef.current = new Map(); return; }

    let cancelled = false;
    let iv;

    const tick = async () => {
      try {
        const r = await api.get("/bookings/me");
        if (cancelled) return;
        const list = r.data || [];
        const known = knownRef.current;
        for (const bk of list) {
          const prev = known.get(bk.booking_id);
          // First time we've seen this booking — remember without notifying
          if (prev === undefined) { known.set(bk.booking_id, bk.status); continue; }
          if (prev === "pending_payment" && bk.status === "confirmed") {
            notifyConfirmed(bk);
          }
          known.set(bk.booking_id, bk.status);
        }
        // Ask for Notification permission the first time we see any pending order
        if (!permAskedRef.current && list.some((b) => b.status === "pending_payment")) {
          permAskedRef.current = true;
          maybeRequestNotifPermission();
        }
      } catch (e) { console.warn("booking watcher fetch failed:", e); }
    };

    tick();
    iv = setInterval(tick, 5000);
    return () => { cancelled = true; if (iv) clearInterval(iv); };
  }, [user]);

  return null;
}

function notifyConfirmed(bk) {
  const total = Number(bk.total || 0).toLocaleString("en-IN");
  const msg = `Order ${bk.booking_id} · ₹${total} confirmed`;
  toast.success(msg, {
    description: "Your payment went through. We're packing it now.",
    duration: 8000,
    action: { label: "View", onClick: () => { window.location.href = "/profile"; } },
  });
  try {
    if (typeof window !== "undefined"
        && "Notification" in window
        && Notification.permission === "granted"
        && document.visibilityState !== "visible") {
      const n = new Notification("Payment confirmed", {
        body: msg,
        icon: "/favicon.ico",
        tag: bk.booking_id,
      });
      n.onclick = () => { window.focus(); window.location.href = "/profile"; };
    }
  } catch (err) { console.warn("Notification API failed:", err); }
}

function maybeRequestNotifPermission() {
  try {
    if (typeof window !== "undefined"
        && "Notification" in window
        && Notification.permission === "default") {
      Notification.requestPermission();
    }
  } catch (err) { console.warn("Notification permission ask failed:", err); }
}
